import React from "react";
const minDistance = 60;
const maxDuration = 700;
const edgeGuard = 24;
/**
 * Left and right swipes as "next" and "previous", for the pages that are stepped through with
 * Forrige and Næste.
 *
 * Swiping left is next: the content follows the finger, as in a phone calendar. The page itself
 * decides what next and previous mean and drives the slide through slideOut and slideIn, because
 * the new content is fetched while the old content is on its way out.
 *
 * Only what is rendered inside slides. Keep Bootstrap modals outside: a transformed ancestor
 * re-anchors position: fixed for as long as the animation runs.
 */
export default class SwipeNavigation extends React.Component {
    constructor() {
        super(...arguments);
        this.containerRef = React.createRef();
        this.contentRef = React.createRef();
        this.start = null;
        this.onTouchStart = (e) => {
            this.start = null;
            if (e.touches.length !== 1)
                return;
            const touch = e.touches[0];
            // The screen edges belong to the browser's own back and forward gesture.
            if (touch.clientX < edgeGuard || touch.clientX > window.innerWidth - edgeGuard)
                return;
            if (this.isOwnedElsewhere(e.target))
                return;
            this.start = { x: touch.clientX, y: touch.clientY, time: Date.now() };
        };
        this.onTouchEnd = (e) => {
            const start = this.start;
            this.start = null;
            if (!start || e.touches.length > 0)
                return;
            const touch = e.changedTouches[0];
            const dx = touch.clientX - start.x;
            const dy = touch.clientY - start.y;
            if (Math.abs(dx) < minDistance || Math.abs(dx) <= 2 * Math.abs(dy))
                return;
            if (Date.now() - start.time > maxDuration)
                return;
            const handler = dx < 0 ? this.props.onNext : this.props.onPrevious;
            !!handler && handler();
        };
        this.onTouchCancel = () => {
            this.start = null;
        };
    }
    canAnimate() {
        const content = this.contentRef.current;
        return !!content
            && typeof content.animate === "function"
            && !window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    }
    // An animation does not advance while the page is hidden, and a cancelled one rejects. The
    // navigation waiting on it must go on either way, so the wait is bounded by a timer.
    settled(animation, duration) {
        return new Promise((resolve) => {
            animation.finished.then(() => resolve(), () => resolve());
            window.setTimeout(resolve, duration + 150);
        });
    }
    // dir is 1 for next and -1 for previous, in both methods.
    slideOut(dir) {
        if (!this.canAnimate())
            return Promise.resolve();
        const duration = 170;
        return this.settled(this.contentRef.current.animate([{ transform: "translateX(0)", opacity: 1 }, { transform: `translateX(${-dir * 100}%)`, opacity: 0 }], { duration: duration, easing: "ease-in", fill: "forwards" }), duration);
    }
    slideIn(dir) {
        if (!this.canAnimate())
            return Promise.resolve();
        const duration = 210;
        const content = this.contentRef.current;
        content.getAnimations().forEach((animation) => animation.cancel());
        return this.settled(content.animate([{ transform: `translateX(${dir * 100}%)`, opacity: 0 }, { transform: "translateX(0)", opacity: 1 }], { duration: duration, easing: "ease-out" }), duration);
    }
    // Dialogs, the date selector and anything that scrolls sideways keep their own touches.
    isOwnedElsewhere(target) {
        if (document.body.classList.contains("modal-open"))
            return true;
        if (target.closest(".modal, .ds-pane, .dropdown-menu"))
            return true;
        for (let elem = target; elem && elem !== this.containerRef.current; elem = elem.parentElement) {
            const overflowX = window.getComputedStyle(elem).overflowX;
            if ((overflowX === "auto" || overflowX === "scroll") && elem.scrollWidth > elem.clientWidth)
                return true;
        }
        return false;
    }
    render() {
        return (React.createElement("div", { className: "bp-swipe", ref: this.containerRef, onTouchStart: this.onTouchStart, onTouchEnd: this.onTouchEnd, onTouchCancel: this.onTouchCancel },
            React.createElement("div", { className: "bp-swipe--content", ref: this.contentRef }, this.props.children)));
    }
}
